import type React from "react";
import {useCallback, useRef} from "react";

import {config} from "@repo/shared";
import * as THREE from "three";

import useMyCamera from "~/contexts/camera/useMyCamera";
import {useGlobalEvent} from "~/contexts/event/GlobalEventBusContext";

import {useSelection} from "../useSelection";

export const MultiSelectionControls: React.FC = () => {
  // These functions and refs come from our selection logic.
  const {
    dragging,
    clearSelection,
    selectArea,
    selectionBoundingBox,
    addAndSelect,
    currentGameObjectType,
  } = useSelection();
  const {targetPosition, targetZoom} = useMyCamera();

  // Refs to track the drag start and end positions.
  const startRef = useRef(new THREE.Vector2());
  const endRef = useRef(new THREE.Vector2());
  // Ref for the visual selection rectangle mesh.
  const selectionMeshRef = useRef<THREE.Mesh>(null);
  // A flag indicating an active selection in progress.
  const isActiveRef = useRef<boolean>(false);

  // Convert client (screen) coordinates to world coordinates.
  const getWorldCoordinates = useCallback(
    (event: {clientX: number; clientY: number}) => {
      const {clientX, clientY} = event;
      const x =
        (clientX - window.innerWidth / 2) / targetZoom.current +
        targetPosition.current.x;
      const y =
        (window.innerHeight / 2 - clientY) / targetZoom.current +
        targetPosition.current.y;
      return {x, y};
    },
    [targetPosition, targetZoom],
  );

  // Begin selection on pointer down if it is the primary pointer.
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!event.isPrimary || dragging.current) {
        dragging.current = false;
        isActiveRef.current = false;
        selectionMeshRef.current?.scale.set(0, 0, 0);
        return;
      }
      const point = getWorldCoordinates(event);
      startRef.current.set(point.x, point.y);
      endRef.current.set(point.x, point.y);
      isActiveRef.current = true;
      selectionMeshRef.current?.scale.set(0, 0, 0);
    },
    [dragging, getWorldCoordinates],
  );

  // Update the selection area as the pointer moves.
  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (dragging.current || !isActiveRef.current) return;
      const point = getWorldCoordinates(event);
      endRef.current.set(point.x, point.y);
      if (selectionMeshRef.current) {
        const centerX = (startRef.current.x + endRef.current.x) / 2;
        const centerY = (startRef.current.y + endRef.current.y) / 2;
        const width = Math.abs(endRef.current.x - startRef.current.x);
        const height = Math.abs(endRef.current.y - startRef.current.y);
        selectionMeshRef.current.position.set(centerX, centerY, 1);
        selectionMeshRef.current.scale.set(width, height, 1);
      }
    },
    [dragging, getWorldCoordinates],
  );

  // On pointer up, if there’s a meaningful drag we finalize the selection.
  const handlePointerUp = useCallback(
    (_event: React.PointerEvent<HTMLDivElement>) => {
      if (isActiveRef.current && !dragging.current) {
        if (endRef.current.clone().sub(startRef.current).length() > 1) {
          const minX = Math.min(startRef.current.x, endRef.current.x);
          const maxX = Math.max(startRef.current.x, endRef.current.x);
          const minY = Math.min(startRef.current.y, endRef.current.y);
          const maxY = Math.max(startRef.current.y, endRef.current.y);
          selectArea({minX, maxX, minY, maxY});
        }
      }
      isActiveRef.current = false;
      selectionMeshRef.current?.scale.set(0, 0, 0);
    },
    [selectArea, dragging],
  );

  // Handle pointer missed events for cases of a quick click (without a drag).
  // If no drag occurred and there is still a selection box, clear it.
  // Otherwise, add and select a new object at the clicked position.
  const handlePointerMissed = useCallback(
    (event: MouseEvent) => {
      if (dragging.current || startRef.current.distanceTo(endRef.current) < 1) {
        if (selectionBoundingBox.current !== null) {
          clearSelection();
        } else {
          const point = getWorldCoordinates({
            clientX: event.clientX,
            clientY: event.clientY,
          });
          const conf = config.objectTypes[currentGameObjectType.current];
          addAndSelect([
            {
              type: currentGameObjectType.current,
              position: [point.x, point.y] as [number, number],
              rotation: 0,
              scale: conf.defaultScale,
            },
          ]);
        }
      }
    },
    [
      dragging,
      selectionBoundingBox,
      clearSelection,
      addAndSelect,
      currentGameObjectType,
      getWorldCoordinates,
    ],
  );

  // Register the multiselection event listeners via global events.
  useGlobalEvent("pointerdown", handlePointerDown);
  useGlobalEvent("pointermove", handlePointerMove);
  useGlobalEvent("pointerup", handlePointerUp);
  useGlobalEvent("pointermissed", handlePointerMissed);

  return (
    <mesh ref={selectionMeshRef} scale={[0, 0, 0]}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial color="blue" transparent opacity={0.2} />
    </mesh>
  );
};

export default MultiSelectionControls;
